import { OriginalSource, SnapQuoteLineSource } from '@prisma/client';
import { LeadTimeFormat } from '@snap-types/models/enums';
import { Nullish } from '@snap-types/types/nullish';
import { createContext, useContext } from 'react';

import { SiteQuoteDocumentOption } from './SiteQuoteDocuments';

export type PDFQuoteLineType = {
  estQtyOnHand: Nullish<number>;
  id: string;
  leadTime: Nullish<number>;
  leadTimeNotes: Nullish<string>;
  lineNumber: number;
  specs: Nullish<string>;
  snapQuoteLineSource: Nullish<SnapQuoteLineSource>;
  part: {
    partId: string;
    originalSource: OriginalSource;
    customerPartId: Nullish<string>;
    description: Nullish<string>;
    drawingId: Nullish<string>;
    drawingRevision: Nullish<string>;
    partSpecs: Nullish<string>;
  };
  partOptions: {
    id: string;
    name: string;
    groupName: string;
  }[];
  quotePrices: {
    id: string;
    quantity: string;
    unitPrice: string;
    total: string;
  }[];
};

export type PDFProviderType = {
  customer: {
    id: Nullish<string>;
    attentionName: Nullish<string>;
    name: Nullish<string>;
    address1: Nullish<string>;
    address2: Nullish<string>;
    city: Nullish<string>;
    state: Nullish<string>;
    postalCode: Nullish<string>;
    countryCode: Nullish<string>;
    email: Nullish<string>;
    phone: Nullish<string>;
    termsNetDays: Nullish<number>;
    salesRepId: Nullish<string>;
    salesRepName: Nullish<string>;
    salesRepEmail: Nullish<string>;
    salesRepPhone: Nullish<string>;
    customerQuoteId: Nullish<string>;
  };
  daysUntilExpiration: Nullish<number>;
  specs: Nullish<string>;
  quoteId: string;
  formattedQuoteTotal: string | null;
  quoteLines: PDFQuoteLineType[];
  leadTimeFormat: LeadTimeFormat;
  siteCode: SiteQuoteDocumentOption;
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
  csrName: Nullish<string>;
  csrEmail: Nullish<string>;
  pricingUnit: string;
};

const PDFContext = createContext<PDFProviderType | null>(null);

type PDFProviderProps = PDFProviderType & {
  children: React.ReactNode;
};

export function PDFProvider({ children, ...props }: PDFProviderProps) {
  return <PDFContext.Provider value={props}>{children}</PDFContext.Provider>;
}

export function usePDFContext() {
  const context = useContext(PDFContext);

  if (!context) {
    throw new Error('usePDFContext must be used within a PDFProvider');
  }

  return context;
}
